// ============================================================
//  ALTIMO Medical Center — ProgramacionQuirofano.jsx
//  Programación visual de cirugías por quirófano
//  ✓ Board de horas con tarjetas de cirugía
//  ✓ Estados: PROGRAMADA / REPROGRAMADA / REALIZADA (con colores)
//  ✓ Modal de programación con búsqueda de paciente ingresado
//  ✓ Reprogramar (conserva ts original), Finalizar, Agregar obs
//  ✓ Observaciones inmutables al guardar — se acumulan con ts
//  ✓ Detección de conflictos (doble reserva)
//  ✓ Reporte histórico filtrable por día/semana/mes y quirófano
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../apiClient';
import Formularios from './Formularios';
import { useConfirmar } from './ModalConfirmar';

// ─── Configuración de quirófanos (extensible) ──────────────
const QUIROFANOS = [
  { id:'q1', label:'Quirófano 1', color:'#993C1D', bg:'#fff5f0' },
  { id:'q2', label:'Quirófano 2', color:'#185FA5', bg:'#f0f6ff' },
  // Agregar más: { id:'q3', label:'Quirófano 3', color:'#1D9E75', bg:'#f0fff8' },
];

// Cobertura 24h — desde 5am hasta 12am (medianoche)
const HORAS = [
  '5am','6am','7am','8am','9am','10am','11am',
  '12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm',
  '8pm','9pm','10pm','11pm','12am',
];

// Slots a partir de los cuales se considera turno nocturno (visual)
const HORA_NOCTURNA_DESDE = '8pm';
const HORAS_NOCTURNAS = new Set(['8pm','9pm','10pm','11pm','12am']);

const ESTADOS = {
  PROGRAMADA:   { label:'Programada',   color:'#1565c0', bg:'#dbeafe', border:'#93c5fd',  icon:'📋' },
  REPROGRAMADA: { label:'Reprogramada', color:'#c45000', bg:'#fff3e0', border:'#fdba74',  icon:'🔄' },
  REALIZADA:    { label:'Realizada',    color:'#166534', bg:'#dcfce7', border:'#86efac',  icon:'✅' },
};

// ─── Helpers ───────────────────────────────────────────────
const hoy = () => new Date().toISOString().slice(0, 10);

const semana = (fecha) => {
  const d  = new Date(fecha + 'T00:00:00');
  const day = d.getDay() === 0 ? 6 : d.getDay() - 1; // lun=0
  const lun = new Date(d); lun.setDate(d.getDate() - day);
  const dom = new Date(lun); dom.setDate(lun.getDate() + 6);
  return { desde: lun.toISOString().slice(0,10), hasta: dom.toISOString().slice(0,10) };
};

const mes = (fecha) => {
  const [y, m] = fecha.split('-');
  const hasta = new Date(y, m, 0).toISOString().slice(0,10);
  return { desde:`${y}-${m}-01`, hasta };
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const parseObs = (json) => { try { return JSON.parse(json||'[]'); } catch(_){ return []; } };

// ─── Estilos base ──────────────────────────────────────────
const S = {
  input: { width:'100%', padding:'9px 12px', border:'1.5px solid #dde3ea', borderRadius:'8px',
    fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:'inherit', background:'#fff' },
  label: { display:'block', fontSize:'11px', fontWeight:'700', color:'#1B2A5E', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'.04em' },
  btn: (bg, fg='#fff') => ({ padding:'8px 18px', background:bg, color:fg, border:'none', borderRadius:'7px',
    cursor:'pointer', fontSize:'12px', fontWeight:'700', fontFamily:'inherit' }),
  card: (estado) => {
    const e = ESTADOS[estado] || ESTADOS.PROGRAMADA;
    return {
      background: e.bg, border:`2px solid ${e.border}`, borderLeft:`5px solid ${e.color}`,
      borderRadius:'8px', padding:'10px 12px', marginBottom:'6px', cursor:'default',
      boxShadow:'0 1px 4px rgba(0,0,0,.08)',
    };
  },
};

// ════════════════════════════════════════════════════════════
//  MODAL: Programar nueva cirugía
// ════════════════════════════════════════════════════════════
function ModalProgramar({ hora, fecha, quirofano, ocupadas, usuario, onGuardado, onCerrar }) {
  const [form, setForm] = useState({
    paciente:'', cirujano:'', anestesiologo:'', otros:'',
    nombre_cirugia:'', responsable:'', admision_id:'',
  });
  const [busqPac, setBusqPac]   = useState('');
  const [resPac,  setResPac]    = useState([]);
  const [cargPac, setCargPac]   = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error,     setError]     = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: String(v).toUpperCase() }));

  const buscarPac = async (q) => {
    setBusqPac(q);
    if (q.length < 2) { setResPac([]); return; }
    setCargPac(true);
    try {
      const r = await api.admisiones.buscar(q);
      setResPac((r?.data ?? r ?? []).filter(p => String(p.estado||'').toLowerCase()==='activa'));
    } catch(_) { setResPac([]); }
    setCargPac(false);
  };

  const selPac = (p) => {
    setForm(f => ({
      ...f,
      paciente:   String(p.paciente||'').toUpperCase(),
      cirujano:   String(p.medico||f.cirujano||'').toUpperCase(),
      responsable: p.habitacion ? ('HAB. '+p.habitacion) : f.responsable,
      admision_id: p.id || f.admision_id,
    }));
    setBusqPac(''); setResPac([]);
  };

  const guardar = async () => {
    if (!form.paciente.trim())       { setError('El nombre del paciente es obligatorio.'); return; }
    if (!form.nombre_cirugia.trim()) { setError('El nombre de la cirugía es obligatorio.'); return; }
    setGuardando(true); setError('');
    try {
      const r = await api.cirugias.crear({
        fecha, hora_slot: hora, quirofano_id: quirofano.id,
        ...form,
        admision_id: form.admision_id || null,
        usuario_id: usuario.id,
      });
      if (!r.ok && r.error) throw new Error(r.error);
      onGuardado();
    } catch(e) { setError(e.message || 'Error al guardar.'); }
    setGuardando(false);
  };

  // Conflicto en la misma hora
  const conflicto = ocupadas.find(c =>
    c.hora_slot === hora && c.quirofano_id === quirofano.id && c.estado !== 'REALIZADA'
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.72)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'16px', padding:'28px', width:'540px', maxHeight:'88vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.35)' }}
        onClick={e=>e.stopPropagation()}>

        <div style={{ fontSize:'17px', fontWeight:'800', color:'#1B2A5E', marginBottom:'4px' }}>
          ➕ Programar cirugía
        </div>
        <div style={{ display:'flex', gap:'10px', marginBottom:'16px', fontSize:'12px' }}>
          <span style={{ background:quirofano.bg, border:`1px solid ${quirofano.color}`, color:quirofano.color, padding:'3px 10px', borderRadius:'20px', fontWeight:'700' }}>
            🏥 {quirofano.label}
          </span>
          <span style={{ background:'#e8f0fb', color:'#1B2A5E', padding:'3px 10px', borderRadius:'20px', fontWeight:'700' }}>
            🕐 {hora} · {fmtFecha(fecha)}
          </span>
        </div>

        {conflicto && (
          <div style={{ background:'#fff3cd', border:'1px solid #ffc107', borderLeft:'4px solid #ff9800', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'12px', color:'#795548' }}>
            ⚠️ <strong>Conflicto:</strong> Ya existe una cirugía {conflicto.estado} en este horario:
            <div style={{ fontWeight:'700', marginTop:'4px' }}>{conflicto.nombre_cirugia} — {conflicto.paciente}</div>
          </div>
        )}

        {error && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderLeft:'4px solid #dc2626', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'12px', color:'#7f1d1d' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Búsqueda paciente ingresado */}
        <div style={{ background:'#e8f0fb', borderRadius:'9px', padding:'12px 14px', marginBottom:'14px' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#1B2A5E', marginBottom:'7px' }}>
            🔍 ¿Paciente ya hospitalizado? Búscalo para auto-rellenar
          </div>
          <input style={S.input} placeholder="Nombre, apellido o DUI del paciente ingresado..."
            value={busqPac} onChange={e => buscarPac(e.target.value)} />
          {cargPac && <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'6px' }}>Buscando...</div>}
          {resPac.map(p => (
            <div key={p.id} onClick={() => selPac(p)}
              style={{ padding:'8px 10px', background:'#fff', border:'1px solid #c8ddf7', borderRadius:'7px',
                marginTop:'6px', cursor:'pointer', fontSize:'12px' }}
              onMouseEnter={e=>e.currentTarget.style.background='#f0f6ff'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
              <span style={{ fontWeight:'700', color:'#1B2A5E' }}>{p.paciente}</span>
              {p.medico && <span style={{ color:'#6c757d', marginLeft:'10px' }}>🩺 {p.medico}</span>}
              {p.habitacion && <span style={{ color:'#1D9E75', marginLeft:'10px' }}>🏠 Hab.{p.habitacion}</span>}
            </div>
          ))}
          {!cargPac && resPac.length===0 && busqPac.length>=2 && (
            <div style={{ fontSize:'11px', color:'#adb5bd', marginTop:'6px' }}>Sin resultados — puedes llenarlo manualmente.</div>
          )}
        </div>

        {/* Formulario */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={S.label}>Nombre de la Cirugía *</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.nombre_cirugia}
              onChange={e=>set('nombre_cirugia',e.target.value)} placeholder="EJ: APENDICECTOMÍA" autoFocus />
          </div>
          <div>
            <label style={S.label}>Paciente *</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.paciente}
              onChange={e=>set('paciente',e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Cirujano</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.cirujano}
              onChange={e=>set('cirujano',e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Anestesiólogo</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.anestesiologo}
              onChange={e=>set('anestesiologo',e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Responsable / Habitación</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.responsable}
              onChange={e=>set('responsable',e.target.value)} />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={S.label}>Otros participantes</label>
            <input style={{ ...S.input, textTransform:'uppercase' }} value={form.otros}
              onChange={e=>set('otros',e.target.value)} placeholder="ENFERMER@, INSTRUMENTISTA..." />
          </div>
        </div>

        <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
          <button style={S.btn('#1D9E75')} onClick={guardar} disabled={guardando}>
            {guardando ? '⏳ Guardando...' : '✓ Programar cirugía'}
          </button>
          <button style={S.btn('#f0f4f8','#495057')} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: Reprogramar
// ════════════════════════════════════════════════════════════
function ModalReprogramar({ cx, usuario, onGuardado, onCerrar }) {
  const [hora_nueva, setHoraNueva] = useState('');
  const [motivo,     setMotivo]    = useState('');
  const [guardando,  setGuardando] = useState(false);
  const [error,      setError]     = useState('');

  const guardar = async () => {
    setGuardando(true); setError('');
    try {
      const r = await api.cirugias.reprogramar({
        id: cx.id, hora_nueva: hora_nueva.toUpperCase(),
        motivo: motivo.toUpperCase(), usuario_id: usuario.id, usuario_nombre: usuario.nombre,
      });
      if (!r.ok && r.error) throw new Error(r.error);
      onGuardado();
    } catch(e) { setError(e.message||'Error.'); }
    setGuardando(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.72)', zIndex:9100, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'14px', padding:'26px', width:'440px', boxShadow:'0 24px 60px rgba(0,0,0,.3)' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ fontSize:'16px', fontWeight:'800', color:'#c45000', marginBottom:'12px' }}>🔄 Reprogramar cirugía</div>
        <div style={{ background:'#fff8f0', border:'1px solid #fdba74', borderRadius:'8px', padding:'10px 12px', marginBottom:'14px', fontSize:'12px', lineHeight:1.6 }}>
          <div><strong>Cirugía:</strong> {cx.nombre_cirugia}</div>
          <div><strong>Paciente:</strong> {cx.paciente}</div>
          <div><strong>Hora actual:</strong> {cx.hora_slot} · {fmtFecha(cx.fecha)}</div>
          <div><strong>Anotada:</strong> <span style={{ color:'#1D9E75', fontWeight:'700' }}>{cx.ts_anotacion}</span></div>
        </div>
        {error && <div style={{ background:'#fee2e2', color:'#7f1d1d', borderRadius:'7px', padding:'8px 12px', marginBottom:'12px', fontSize:'12px' }}>⚠️ {error}</div>}
        <div style={{ marginBottom:'12px' }}>
          <label style={S.label}>Nueva hora / fecha propuesta</label>
          <input style={{ ...S.input, textTransform:'uppercase' }} value={hora_nueva} autoFocus
            onChange={e=>setHoraNueva(e.target.value)} placeholder="EJ: VIERNES 10AM / 20-MAY 9:30AM" />
        </div>
        <div style={{ marginBottom:'16px' }}>
          <label style={S.label}>Motivo de reprogramación</label>
          <input style={{ ...S.input, textTransform:'uppercase' }} value={motivo}
            onChange={e=>setMotivo(e.target.value)} placeholder="OPCIONAL" />
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={S.btn('#c45000')} onClick={guardar} disabled={guardando}>
            {guardando ? '⏳...' : '🔄 Confirmar reprogramación'}
          </button>
          <button style={S.btn('#f0f4f8','#495057')} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: Finalizar cirugía
// ════════════════════════════════════════════════════════════
function ModalFinalizar({ cx, usuario, onGuardado, onCerrar }) {
  const [hora_fin, setHoraFin] = useState('');
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    setGuardando(true);
    try {
      await api.cirugias.finalizar({ id: cx.id, hora_fin, usuario_id: usuario.id });
      onGuardado();
    } catch(e) { alert(e.message||'Error'); }
    setGuardando(false);
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.72)', zIndex:9100, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'14px', padding:'24px', width:'400px', boxShadow:'0 24px 60px rgba(0,0,0,.3)' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ fontSize:'16px', fontWeight:'800', color:'#166534', marginBottom:'12px' }}>✅ Registrar finalización</div>
        <div style={{ background:'#dcfce7', border:'1px solid #86efac', borderRadius:'8px', padding:'10px 12px', marginBottom:'14px', fontSize:'12px', lineHeight:1.6 }}>
          <div><strong>Cirugía:</strong> {cx.nombre_cirugia}</div>
          <div><strong>Paciente:</strong> {cx.paciente}</div>
          <div><strong>Hora programada:</strong> {cx.hora_slot}</div>
        </div>
        <div style={{ marginBottom:'16px' }}>
          <label style={S.label}>Hora de finalización (opcional — si vacío usa la hora actual)</label>
          <input style={{ ...S.input, textTransform:'uppercase' }} value={hora_fin}
            onChange={e=>setHoraFin(e.target.value)} placeholder="EJ: 11:45AM" autoFocus />
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={S.btn('#166534')} onClick={guardar} disabled={guardando}>
            {guardando ? '⏳...' : '✅ Confirmar realizada'}
          </button>
          <button style={S.btn('#f0f4f8','#495057')} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: Detalle + Observaciones de una cirugía
// ════════════════════════════════════════════════════════════
function ModalDetalle({ cx, usuario, onCerrar, onCambiado }) {
  const [nuevaObs, setNuevaObs] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cxLocal, setCxLocal] = useState(cx);
  const obs = parseObs(cxLocal.observaciones);

  const agregarObs = async () => {
    if (!nuevaObs.trim()) return;
    setGuardando(true);
    try {
      await api.cirugias.agregarObs({
        id: cxLocal.id, texto: nuevaObs,
        usuario_id: usuario.id, usuario_nombre: usuario.nombre,
      });
      // Refrescar local
      const tsN = new Date().toLocaleString('es-SV');
      const obsN = [...obs, { texto: nuevaObs.toUpperCase(), ts: tsN, usuario: usuario.nombre }];
      setCxLocal(c => ({ ...c, observaciones: JSON.stringify(obsN) }));
      setNuevaObs('');
      if (onCambiado) onCambiado();
    } catch(e) { alert(e.message||'Error'); }
    setGuardando(false);
  };

  const est = ESTADOS[cxLocal.estado] || ESTADOS.PROGRAMADA;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,20,55,.72)', zIndex:9100, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'16px', padding:'26px', width:'520px', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 28px 70px rgba(0,0,0,.32)' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'16px', fontWeight:'800', color:'#1B2A5E' }}>{cxLocal.nombre_cirugia}</div>
            <div style={{ fontSize:'12px', color:'#6c757d', marginTop:'2px' }}>{cxLocal.hora_slot} · {fmtFecha(cxLocal.fecha)}</div>
          </div>
          <div style={{ background:est.bg, color:est.color, border:`1px solid ${est.border}`, borderRadius:'20px', padding:'4px 12px', fontSize:'11px', fontWeight:'700' }}>
            {est.icon} {est.label}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px', fontSize:'12px' }}>
          {[['👤 Paciente',cxLocal.paciente],['🩺 Cirujano',cxLocal.cirujano||'—'],
            ['💉 Anestesiólogo',cxLocal.anestesiologo||'—'],['👥 Otros',cxLocal.otros||'—'],
            ['📍 Responsable',cxLocal.responsable||'—'],['🕐 Anotada',cxLocal.ts_anotacion],
            ...(cxLocal.hora_fin ? [['🏁 Fin',cxLocal.hora_fin]] : []),
            ...(cxLocal.hora_nueva ? [['🔄 Nueva hora',cxLocal.hora_nueva]] : []),
          ].map(([k,v]) => (
            <div key={k} style={{ background:'#f8fafd', borderRadius:'7px', padding:'8px 10px' }}>
              <div style={{ fontSize:'10px', color:'#6c757d', fontWeight:'700' }}>{k}</div>
              <div style={{ fontWeight:'600', color:'#1B2A5E', marginTop:'2px' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Observaciones inmutables */}
        <div style={{ marginBottom:'12px' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'8px' }}>
            📝 Observaciones ({obs.length})
          </div>
          {obs.length === 0
            ? <div style={{ fontSize:'11px', color:'#adb5bd', fontStyle:'italic' }}>Sin observaciones.</div>
            : obs.map((o, i) => (
              <div key={i} style={{ borderLeft:`3px solid ${o.tipo==='SISTEMA'?'#1D9E75':'#1B2A5E'}`, paddingLeft:'10px', marginBottom:'8px', fontSize:'12px' }}>
                <div style={{ color:'#1B2A5E' }}>{o.texto}</div>
                <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'2px' }}>{o.ts}{o.usuario ? ' · '+o.usuario : ''}</div>
              </div>
            ))}
        </div>

        {cxLocal.estado !== 'REALIZADA' && (
          <div style={{ borderTop:'1px solid #eee', paddingTop:'12px' }}>
            <label style={S.label}>Agregar observación (quedará guardada y no podrá editarse)</label>
            <div style={{ display:'flex', gap:'8px' }}>
              <input style={{ ...S.input, flex:1, textTransform:'uppercase' }} value={nuevaObs} autoFocus
                onChange={e=>setNuevaObs(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&agregarObs()}
                placeholder="ESCRIBIR OBSERVACIÓN Y PRESIONAR ENTER..." />
              <button style={S.btn('#1B2A5E')} onClick={agregarObs} disabled={guardando||!nuevaObs.trim()}>
                {guardando?'⏳':'+ Agregar'}
              </button>
            </div>
          </div>
        )}

        <button style={{ ...S.btn('#f0f4f8','#495057'), marginTop:'14px', width:'100%' }} onClick={onCerrar}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  TARJETA DE CIRUGÍA
// ════════════════════════════════════════════════════════════
function TarjetaCirugia({ cx, usuario, onCambiado }) {
  const [modal, setModal] = useState(null); // 'detalle'|'reprog'|'fin'
  const est = ESTADOS[cx.estado] || ESTADOS.PROGRAMADA;
  const { confirmar: confirmarAccion, ModalConfirmarUI } = useConfirmar();

  const confirmarEliminar = async () => {
    if (!(await confirmarAccion({
      texto: `¿Eliminar la cirugía "${cx.nombre_cirugia}"?`,
      tipo: 'peligro',
      textoConfirmar: 'Eliminar cirugía',
    }))) return;
    try {
      await api.cirugias.eliminar(cx.id);
      onCambiado();
    } catch(e) { alert(e.message||'Error al eliminar'); }
  };

  return (
    <>
      {ModalConfirmarUI}
      <div style={S.card(cx.estado)}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'4px' }}>
          <div style={{ fontWeight:'800', fontSize:'12px', color:est.color }}>{est.icon} {cx.nombre_cirugia}</div>
          <div style={{ background:est.bg, color:est.color, border:`1px solid ${est.border}`, borderRadius:'99px',
            padding:'1px 8px', fontSize:'10px', fontWeight:'700', whiteSpace:'nowrap', marginLeft:'6px' }}>
            {est.label}
          </div>
        </div>

        {/* Datos */}
        <div style={{ fontSize:'11px', color:'#495057', lineHeight:1.7 }}>
          <div>👤 <strong>{cx.paciente}</strong></div>
          {cx.cirujano && <div>🩺 {cx.cirujano}</div>}
          {cx.anestesiologo && <div>💉 {cx.anestesiologo}</div>}
          {cx.hora_nueva && (
            <div style={{ color:'#c45000', fontWeight:'700' }}>🔄 Reprog. → {cx.hora_nueva}</div>
          )}
          {cx.hora_fin && (
            <div style={{ color:'#166534', fontWeight:'700' }}>🏁 Finalizada: {cx.hora_fin}</div>
          )}
        </div>

        {/* Acciones */}
        <div style={{ display:'flex', gap:'5px', marginTop:'8px', flexWrap:'wrap' }}>
          <button style={{ ...S.btn('#1B2A5E'), fontSize:'10px', padding:'4px 9px' }}
            onClick={() => setModal('detalle')}>
            👁 Ver detalles
          </button>
          {cx.estado !== 'REALIZADA' && (
            <>
              <button style={{ ...S.btn('#fff3e0','#c45000'), border:'1px solid #fdba74', fontSize:'10px', padding:'4px 9px' }}
                onClick={() => setModal('reprog')}>
                🔄 Reprogramar
              </button>
              <button style={{ ...S.btn('#dcfce7','#166534'), border:'1px solid #86efac', fontSize:'10px', padding:'4px 9px' }}
                onClick={() => setModal('fin')}>
                ✅ Finalizar
              </button>
            </>
          )}
          {cx.estado !== 'REALIZADA' && (
            <button style={{ ...S.btn('#fee2e2','#dc2626'), border:'1px solid #fca5a5', fontSize:'10px', padding:'4px 9px' }}
              onClick={confirmarEliminar}>
              🗑
            </button>
          )}
        </div>
      </div>

      {modal === 'detalle'  && <ModalDetalle    cx={cx} usuario={usuario} onCerrar={()=>setModal(null)} onCambiado={onCambiado} />}
      {modal === 'reprog'   && <ModalReprogramar cx={cx} usuario={usuario} onCerrar={()=>setModal(null)} onGuardado={()=>{setModal(null);onCambiado();}} />}
      {modal === 'fin'      && <ModalFinalizar   cx={cx} usuario={usuario} onCerrar={()=>setModal(null)} onGuardado={()=>{setModal(null);onCambiado();}} />}
    </>
  );
}

// ════════════════════════════════════════════════════════════
//  PANEL DE REPORTE HISTÓRICO
// ════════════════════════════════════════════════════════════
function PanelReporte({ usuario }) {
  const [modo,    setModo]    = useState('dia');  // dia|semana|mes
  const [fecha,   setFecha]   = useState(hoy());
  const [qx,      setQx]      = useState('todos');
  const [datos,   setDatos]   = useState([]);
  const [cargando,setCargando]= useState(false);

  const rango = useMemo(() => {
    if (modo === 'dia')    return { desde: fecha, hasta: fecha };
    if (modo === 'semana') return semana(fecha);
    return mes(fecha);
  }, [modo, fecha]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.cirugias.reporte({ ...rango, quirofano_id: qx });
      setDatos(r?.data ?? r ?? []);
    } catch(_) { setDatos([]); }
    setCargando(false);
  }, [rango, qx]);

  useEffect(() => { cargar(); }, [cargar]);

  const estadisticas = useMemo(() => {
    const total = datos.length;
    const realizadas   = datos.filter(c=>c.estado==='REALIZADA').length;
    const reprogramadas= datos.filter(c=>c.estado==='REPROGRAMADA').length;
    const programadas  = datos.filter(c=>c.estado==='PROGRAMADA').length;
    // Por quirófano
    const porQx = {};
    QUIROFANOS.forEach(q => { porQx[q.id] = datos.filter(c=>c.quirofano_id===q.id).length; });
    return { total, realizadas, reprogramadas, programadas, porQx };
  }, [datos]);

  return (
    <div style={{ padding:'0 4px' }}>
      {/* Filtros */}
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center', marginBottom:'16px' }}>
        <div style={{ display:'flex', borderRadius:'8px', overflow:'hidden', border:'1px solid #dde3ea' }}>
          {['dia','semana','mes'].map(m => (
            <button key={m} onClick={()=>setModo(m)}
              style={{ padding:'7px 16px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:modo===m?'700':'400',
                background:modo===m?'#1B2A5E':'#fff', color:modo===m?'#fff':'#495057' }}>
              {m==='dia'?'Día':m==='semana'?'Semana':'Mes'}
            </button>
          ))}
        </div>
        <input type="date" value={fecha} onChange={e=>setFecha(e.target.value)}
          style={{ ...S.input, width:'150px' }} />
        <select value={qx} onChange={e=>setQx(e.target.value)} style={{ ...S.input, width:'160px' }}>
          <option value="todos">Todos los quirófanos</option>
          {QUIROFANOS.map(q=><option key={q.id} value={q.id}>{q.label}</option>)}
        </select>
        <div style={{ fontSize:'12px', color:'#6c757d' }}>
          {rango.desde !== rango.hasta ? `${fmtFecha(rango.desde)} – ${fmtFecha(rango.hasta)}` : fmtFecha(rango.desde)}
        </div>
      </div>

      {/* Estadísticas */}
      <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'16px' }}>
        {[
          { label:'Total', val:estadisticas.total, color:'#1B2A5E', bg:'#e8f0fb' },
          { label:'✅ Realizadas', val:estadisticas.realizadas, color:'#166534', bg:'#dcfce7' },
          { label:'🔄 Reprogramadas', val:estadisticas.reprogramadas, color:'#c45000', bg:'#fff3e0' },
          { label:'📋 Programadas', val:estadisticas.programadas, color:'#1565c0', bg:'#dbeafe' },
          ...QUIROFANOS.map(q=>({ label:q.label, val:estadisticas.porQx[q.id]||0, color:q.color, bg:q.bg }))
        ].map((s,i) => (
          <div key={i} style={{ background:s.bg, borderRadius:'10px', padding:'10px 16px', minWidth:'90px', textAlign:'center' }}>
            <div style={{ fontSize:'22px', fontWeight:'800', color:s.color }}>{s.val}</div>
            <div style={{ fontSize:'10px', color:s.color, fontWeight:'600' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabla */}
      {cargando ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>⏳ Cargando...</div>
      ) : datos.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#adb5bd', fontSize:'14px' }}>
          Sin cirugías en el período seleccionado.
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr style={{ background:'#1B2A5E', color:'#fff' }}>
                {['Fecha','Hora','Quirófano','Cirugía','Paciente','Cirujano','Anestesiólogo','Estado','Fin'].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontWeight:'700', fontSize:'11px', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.map((c,i) => {
                const est = ESTADOS[c.estado] || ESTADOS.PROGRAMADA;
                const qInfo = QUIROFANOS.find(q=>q.id===c.quirofano_id)||QUIROFANOS[0];
                return (
                  <tr key={c.id} style={{ background:i%2===0?'#fff':'#f8fafd', borderLeft:`3px solid ${est.color}` }}>
                    <td style={{ padding:'7px 10px', whiteSpace:'nowrap' }}>{fmtFecha(c.fecha)}</td>
                    <td style={{ padding:'7px 10px', fontWeight:'700' }}>{c.hora_slot}</td>
                    <td style={{ padding:'7px 10px' }}>
                      <span style={{ background:qInfo.bg, color:qInfo.color, borderRadius:'5px', padding:'2px 8px', fontSize:'10px', fontWeight:'700' }}>
                        {qInfo.label}
                      </span>
                    </td>
                    <td style={{ padding:'7px 10px', fontWeight:'600' }}>{c.nombre_cirugia}</td>
                    <td style={{ padding:'7px 10px' }}>{c.paciente}</td>
                    <td style={{ padding:'7px 10px' }}>{c.cirujano||'—'}</td>
                    <td style={{ padding:'7px 10px' }}>{c.anestesiologo||'—'}</td>
                    <td style={{ padding:'7px 10px' }}>
                      <span style={{ background:est.bg, color:est.color, border:`1px solid ${est.border}`, borderRadius:'99px', padding:'2px 9px', fontSize:'10px', fontWeight:'700', whiteSpace:'nowrap' }}>
                        {est.icon} {est.label}
                      </span>
                    </td>
                    <td style={{ padding:'7px 10px', color:'#166534', fontWeight:'600' }}>{c.hora_fin||'—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  BOARD DE PROGRAMACIÓN (vista de un día + quirófano)
// ════════════════════════════════════════════════════════════
function BoardProgramacion({ fecha, quirofano, usuario, cirugias, onCambiado }) {
  const [modalProg,    setModalProg]    = useState(null);  // hora_slot
  const [mostrarNoche, setMostrarNoche] = useState(false); // toggle turno nocturno

  // Construir mapa hora→cirugías; capturar también slots "huérfanos" fuera del listado
  const { cxPorHora, horasHuerfanas } = useMemo(() => {
    const m = {};
    HORAS.forEach(h => { m[h] = []; });
    const huerfanas = new Set();
    cirugias.filter(c => c.quirofano_id === quirofano.id).forEach(c => {
      if (m[c.hora_slot] !== undefined) {
        m[c.hora_slot].push(c);
      } else {
        // Slot fuera del rango conocido → crear entrada dinámica
        if (!m[c.hora_slot]) m[c.hora_slot] = [];
        m[c.hora_slot].push(c);
        huerfanas.add(c.hora_slot);
      }
    });
    return { cxPorHora: m, horasHuerfanas: huerfanas };
  }, [cirugias, quirofano]);

  // Detectar si hay cirugías nocturnas para mostrar el aviso automáticamente
  const hayCirugíasNocturnas = useMemo(() =>
    [...HORAS_NOCTURNAS, ...horasHuerfanas].some(h => (cxPorHora[h]||[]).length > 0),
  [cxPorHora, horasHuerfanas]);

  // Lista final de horas a renderizar
  const horasRender = useMemo(() => {
    const base = mostrarNoche || hayCirugíasNocturnas
      ? HORAS
      : HORAS.filter(h => !HORAS_NOCTURNAS.has(h));
    // Agregar huérfanas al final si existen
    return [...base, ...[...horasHuerfanas].filter(h => !base.includes(h))];
  }, [mostrarNoche, hayCirugíasNocturnas, horasHuerfanas]);

  const esNocturna = (h) => HORAS_NOCTURNAS.has(h) || horasHuerfanas.has(h);
  const esPrimeraNocturna = (h, idx) => esNocturna(h) && (idx === 0 || !esNocturna(horasRender[idx-1]));

  return (
    <div style={{ border:`1px solid ${quirofano.color}`, borderTop:'none', borderRadius:'0 0 10px 10px', background:'#fff', minHeight:'300px' }}>

      {/* ── Toggle turno nocturno ── */}
      {!hayCirugíasNocturnas && (
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'8px 14px 0', borderBottom:'1px solid #f0f4f8' }}>
          <button
            onClick={() => setMostrarNoche(v => !v)}
            style={{ display:'flex', alignItems:'center', gap:'6px', padding:'5px 14px',
              background: mostrarNoche ? '#1B2A5E' : '#f0f4f8',
              color:      mostrarNoche ? '#fff'    : '#495057',
              border:'none', borderRadius:'20px', cursor:'pointer', fontSize:'11px', fontWeight:'700', fontFamily:'inherit',
              transition:'all .2s' }}>
            🌙 {mostrarNoche ? 'Ocultar turno nocturno' : 'Mostrar turno nocturno (8pm – 12am)'}
          </button>
        </div>
      )}

      {hayCirugíasNocturnas && (
        <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 14px',
          background:'#1B2A5E', color:'#93c5fd', fontSize:'11px', fontWeight:'700' }}>
          🌙 Hay cirugías programadas en turno nocturno — mostrando horario extendido
        </div>
      )}

      {horasRender.map((hora, hi) => {
        const lista = cxPorHora[hora] || [];
        const ocupada = lista.some(c => c.estado !== 'REALIZADA');
        const nocturna = esNocturna(hora);
        const primeraNocturna = esPrimeraNocturna(hora, hi);

        return (
          <React.Fragment key={hora}>
            {/* Separador visual de inicio de turno nocturno */}
            {primeraNocturna && (
              <div style={{ display:'flex', alignItems:'center', gap:'10px',
                padding:'7px 14px', background:'#0f172a', color:'#7dd3fc',
                fontSize:'11px', fontWeight:'800', letterSpacing:'.05em' }}>
                🌙 TURNO NOCTURNO — 8:00 PM en adelante
                <span style={{ marginLeft:'auto', fontSize:'10px', color:'#475569', fontWeight:'400' }}>
                  Disponible para cirugías de urgencia / electivas tardías
                </span>
              </div>
            )}

            <div style={{
              display:'flex',
              borderBottom: hi < horasRender.length-1 ? `1px solid ${nocturna?'#1e293b':'#f0f4f8'}` : 'none',
              minHeight:'52px', alignItems:'flex-start',
              background: nocturna ? (lista.length?'#0f172a':'#0c1322') : 'transparent',
              transition:'background .2s',
            }}>
              {/* Etiqueta de hora */}
              <div style={{ width:'64px', flexShrink:0, padding:'10px 8px', textAlign:'right',
                fontWeight:'700', fontSize:'12px', alignSelf:'stretch',
                display:'flex', alignItems:'center', justifyContent:'flex-end',
                color:      nocturna ? '#7dd3fc' : '#1B2A5E',
                background: nocturna ? '#0a0f1e' : '#f8fafd',
                borderRight:`2px solid ${nocturna?'#1e3a5f':'#e8f0fb'}` }}>
                {hora}
              </div>

              {/* Contenido */}
              <div style={{ flex:1, padding:'6px 10px' }}>
                {lista.map(cx => (
                  <TarjetaCirugia key={cx.id} cx={cx} usuario={usuario} onCambiado={onCambiado} />
                ))}

                {!ocupada && (
                  <button
                    onClick={() => setModalProg(hora)}
                    style={{ display:'flex', alignItems:'center', gap:'6px', padding:'5px 14px',
                      background:'transparent',
                      border:`1.5px dashed ${nocturna?'#334155':'#c0ccda'}`,
                      borderRadius:'7px',
                      color: nocturna ? '#475569' : '#94a3b8',
                      fontSize:'11px', cursor:'pointer', transition:'all .15s', fontFamily:'inherit',
                      width: lista.length ? '160px' : '100%', marginTop: lista.length ? '4px' : '0' }}
                    onMouseEnter={e=>{
                      e.currentTarget.style.background=nocturna?'#1e293b':quirofano.bg;
                      e.currentTarget.style.borderColor=nocturna?'#7dd3fc':quirofano.color;
                      e.currentTarget.style.color=nocturna?'#7dd3fc':quirofano.color;
                    }}
                    onMouseLeave={e=>{
                      e.currentTarget.style.background='transparent';
                      e.currentTarget.style.borderColor=nocturna?'#334155':'#c0ccda';
                      e.currentTarget.style.color=nocturna?'#475569':'#94a3b8';
                    }}>
                    {nocturna ? '🌙' : '➕'} Programar cirugía
                  </button>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {modalProg && (
        <ModalProgramar
          hora={modalProg} fecha={fecha} quirofano={quirofano}
          ocupadas={cirugias} usuario={usuario}
          onCerrar={()=>setModalProg(null)}
          onGuardado={()=>{ setModalProg(null); onCambiado(); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function ProgramacionQuirofano({ usuario }) {
  const [vista,    setVista]    = useState('board');     // 'board' | 'formularios' | 'reporte'
  const [fecha,    setFecha]    = useState(hoy());
  const [qxTab,    setQxTab]    = useState(QUIROFANOS[0].id);
  const [cirugias, setCirugias] = useState([]);
  const [cargando, setCargando] = useState(false);

  const qxActivo = QUIROFANOS.find(q => q.id === qxTab) || QUIROFANOS[0];

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.cirugias.listar({ fecha, quirofano_id: 'todos' });
      setCirugias(r?.data ?? r ?? []);
    } catch(_) { setCirugias([]); }
    setCargando(false);
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  // Navegar entre fechas
  const moverFecha = (dias) => {
    const d = new Date(fecha + 'T12:00:00');
    d.setDate(d.getDate() + dias);
    setFecha(d.toISOString().slice(0,10));
  };

  // Resumen rápido por quirófano para los tabs
  const cxPorQx = useMemo(() => {
    const m = {};
    QUIROFANOS.forEach(q => {
      const lista = cirugias.filter(c => c.quirofano_id === q.id);
      m[q.id] = { total: lista.length,
        realizadas:   lista.filter(c=>c.estado==='REALIZADA').length,
        reprogramadas:lista.filter(c=>c.estado==='REPROGRAMADA').length,
      };
    });
    return m;
  }, [cirugias]);

  return (
    <div style={{ padding:'20px', fontFamily:'Segoe UI, sans-serif' }}>
      {/* ── Topbar ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <div style={{ fontSize:'20px', fontWeight:'800', color:'#1B2A5E' }}>🏥 Programación de Quirófanos</div>
          <div style={{ fontSize:'12px', color:'#6c757d', marginTop:'2px' }}>ALTIMO Medical Center</div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={{ ...S.btn(vista==='board'?'#1B2A5E':'#f0f4f8', vista==='board'?'#fff':'#495057') }}
            onClick={() => setVista('board')}>
            📅 Programación
          </button>
          <button style={{ ...S.btn(vista==='formularios'?'#993C1D':'#f0f4f8', vista==='formularios'?'#fff':'#495057') }}
            onClick={() => setVista('formularios')}>
            📋 Formularios QX
          </button>
          <button style={{ ...S.btn(vista==='reporte'?'#1B2A5E':'#f0f4f8', vista==='reporte'?'#fff':'#495057') }}
            onClick={() => setVista('reporte')}>
            📊 Historial / Reportes
          </button>
        </div>
      </div>

      {vista === 'board' ? (
        <>
          {/* ── Selector de fecha ── */}
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px', background:'#fff', padding:'12px 16px', borderRadius:'12px', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
            <button style={S.btn('#e8f0fb','#1B2A5E')} onClick={()=>moverFecha(-1)}>◀</button>
            <input type="date" value={fecha} onChange={e=>setFecha(e.target.value)}
              style={{ ...S.input, width:'160px' }} />
            <button style={S.btn('#e8f0fb','#1B2A5E')} onClick={()=>moverFecha(1)}>▶</button>
            <button style={S.btn('#1D9E75')} onClick={()=>setFecha(hoy())}>Hoy</button>
            <div style={{ fontSize:'14px', fontWeight:'700', color:'#1B2A5E', marginLeft:'4px' }}>
              {new Date(fecha+'T12:00:00').toLocaleDateString('es-SV',{weekday:'long',day:'2-digit',month:'long',year:'numeric'}).toUpperCase()}
            </div>
            {cargando && <div style={{ fontSize:'12px', color:'#6c757d', marginLeft:'8px' }}>⏳ Cargando...</div>}
          </div>

          {/* ── Tabs de quirófanos ── */}
          <div style={{ display:'flex', gap:'4px', marginBottom:0, borderBottom:`2px solid ${qxActivo.color}` }}>
            {QUIROFANOS.map(q => {
              const stats = cxPorQx[q.id] || {};
              const activo = q.id === qxTab;
              return (
                <button key={q.id} onClick={()=>setQxTab(q.id)}
                  style={{ padding:'10px 22px', border:'none', cursor:'pointer', borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                    fontWeight:'700', fontSize:'13px', transition:'all .15s',
                    background: activo ? q.color : '#e8f0fb',
                    color:      activo ? '#fff'  : '#495057',
                    boxShadow:  activo ? `0 -2px 8px ${q.color}40` : 'none' }}>
                  🏥 {q.label}
                  {stats.total > 0 && (
                    <span style={{ marginLeft:'8px', background:'rgba(255,255,255,.3)', color:activo?'#fff':q.color,
                      borderRadius:'99px', padding:'1px 8px', fontSize:'10px' }}>
                      {stats.total}
                      {stats.realizadas > 0 && <span> ✅{stats.realizadas}</span>}
                      {stats.reprogramadas > 0 && <span> 🔄{stats.reprogramadas}</span>}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Board del quirófano activo ── */}
          <BoardProgramacion
            fecha={fecha} quirofano={qxActivo} usuario={usuario}
            cirugias={cirugias} onCambiado={cargar}
          />
        </>
      ) : vista === 'formularios' ? (
        <div style={{ background:'#fff', borderRadius:'12px', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <div style={{ background:'#993C1D', color:'#fff', padding:'10px 20px', fontSize:'13px', fontWeight:'700' }}>
            📋 Formularios de Quirófano — Hoja de Gastos · Reporte de Anestesia · Reporte Operatorio
          </div>
          <Formularios usuario={usuario} areaFiltro="quirofano" excluirForms={['prog_cirugias']} />
        </div>
      ) : (
        <div style={{ background:'#fff', borderRadius:'12px', padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <PanelReporte usuario={usuario} />
        </div>
      )}
    </div>
  );
}
